import { createFileRoute } from "@tanstack/react-router";
import BuilderPortalLayout from "lib/components/builders/BuilderPortalLayout";
import {
  builderAccountPath,
  builderDeveloperPath,
  builderIntegrationsPath,
  builderPlanLibraryPath,
  builderOptionCatalogPath,
  locationsPath,
  procurePath,
  studioFilesPath,
  builderUsersPath,
  useAppRouterParams,
  builderAutoRendersPath,
} from "lib/routeHelpers";
import { userCanUseStudio, userCanViewAutoRenders } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import ProductCard, { ProductCards } from "ui/cards/ProductCard";
import withAuthentication from "../../../components/withAuthentication";

function DashboardPage() {
  const { builderId } = useAppRouterParams();

  if (!builderId) {
    return null;
  }

  return <ReadyDashboardPage builderId={builderId} />;
}

function ReadyDashboardPage({ builderId }: { builderId: string }) {
  const userContext = useUserContext();
  const { hasPermission, enabledProducts, enabledFeatures, builder } = userContext;

  return (
    <BuilderPortalLayout selected="dashboard" pageTitle="Dashboard">
      <div className="builder-portal-layout-content spacing-top">
        <ProductCards>
          {hasPermission("VIEW_COMMUNITIES") && (
            <ProductCard
              title="Locations"
              subtitle="Manage lots, maps and home plans."
              href={locationsPath(builderId)}
            />
          )}
          {userCanUseStudio(userContext) && (
            <ProductCard title="Studio Files" subtitle="Design homes, manage CDs." href={studioFilesPath(builderId)} />
          )}
          {hasPermission("VIEW_MASTER_PLANS") && (
            <ProductCard
              title="Plan Library"
              subtitle="The plans that run your business."
              href={builderPlanLibraryPath(builderId)}
            />
          )}
          {hasPermission("VIEW_OPTION_CATALOG") && enabledFeatures.nextGenOptions && (
            <ProductCard
              title="Option Catalog"
              subtitle="Manage all your options."
              href={`${builderOptionCatalogPath(builderId)}`}
            />
          )}

          {hasPermission("VIEW_PROCURE") && enabledProducts.procure && (
            <ProductCard
              title="Estimate"
              subtitle="Generate your bill of materials."
              href={procurePath({ builderId })}
            />
          )}
          <ProductCard title="Account Settings" subtitle="Manage your account." href={builderAccountPath(builderId)} />

          {hasPermission("VIEW_STAFF_PERMISSIONS") && (
            <ProductCard
              title="User Management"
              subtitle="Manage users and roles."
              href={builderUsersPath(builderId)}
            />
          )}
          {hasPermission("VIEW_CLIENT_APPS") && enabledProducts.api && (
            <ProductCard
              title="Developer Portal"
              subtitle="Your API keys and documentation."
              href={builderDeveloperPath(builderId)}
            />
          )}
          {hasPermission("VIEW_INTEGRATIONS") && enabledFeatures.integrations && (
            <ProductCard
              title="ERP Integrations"
              subtitle="Manage ERP integrations."
              href={builderIntegrationsPath(builderId)}
            />
          )}
          {userCanViewAutoRenders({ hasPermission, builder, enabledFeatures }) && (
            <ProductCard
              title="AutoRender"
              subtitle="Generate and organize assets."
              href={builderAutoRendersPath(builderId)}
            />
          )}
        </ProductCards>
      </div>
    </BuilderPortalLayout>
  );
}

export const Route = createFileRoute("/builders/$builder_id/")({
  component: withAuthentication(DashboardPage),
});
